import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { useTaal } from "@/contexts/TaalContext";

/**
 * De licentie van de organisatie (F3c, Frank 20 oktober 2026).
 *
 * Een licentie per organisatie (keuze 1). Zolang er geen is, houdt elk account zijn
 * eigen plan; na activeren geldt zij voor iedereen op deze installatie (keuze 2a). Iedereen ziet hier wat er geldt; alleen een eigenaar
 * activeert een licentiebestand. Na de verloopdatum blijft zij veertien dagen
 * gelden met een melding (keuze 5), daarna werkt de installatie als Free.
 */

const DAG = 86400;

export function datumIn(taal, seconden) {
  return new Date(seconden * 1000).toLocaleDateString(taal === "en" ? "en-GB" : "nl-NL",
                                                      { day: "numeric", month: "long", year: "numeric" });
}

/** Wat er over de licentie te zeggen is, als een zin. PUUR op `nu` na. */
export function licentieRegel(status, t, taal, nu = Date.now() / 1000) {
  if (!status) return "";
  const niveau = status.tier_label || status.tier;
  if (status.stand === "geldig") {
    return status.expires_at > 0
      ? t("{niveau}, geldig tot {datum}.", { niveau, datum: datumIn(taal, status.expires_at) })
      : t("{niveau}, zonder verloopdatum.", { niveau });
  }
  if (status.stand === "respijt") {
    const dagen = Math.max(1, Math.ceil((status.respijt_tot - nu) / DAG));
    return t("{niveau} is verlopen op {datum}. Nog {dagen} dagen respijt, tot {tot}; daarna werkt deze installatie als Free.",
             { niveau, datum: datumIn(taal, status.expires_at), dagen, tot: datumIn(taal, status.respijt_tot) });
  }
  if (status.stand === "verlopen") {
    return t("{niveau} is verlopen op {datum}; deze installatie werkt als Free.",
             { niveau, datum: datumIn(taal, status.expires_at) });
  }
  // Gewijzigde keuze 2a (Frank, 20 oktober 2026): zonder organisatielicentie houdt
  // elk account zijn eigen plan.
  return t("Geen organisatielicentie: elk account houdt zijn eigen plan.");
}

export default function Licentie({ eigenaar }) {
  const { t, taal } = useTaal();
  const [status, setStatus] = useState(null);
  const [bezig, setBezig] = useState(false);
  const kiezer = useRef(null);

  const laden = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/license/status`);
      setStatus(data);
    } catch {
      setStatus(null);
    }
  }, []);

  useEffect(() => { laden(); }, [laden]);

  const activeer = async (bestand) => {
    if (!bestand) return;
    setBezig(true);
    try {
      const data = JSON.parse(await bestand.text());
      await axios.post(`${API}/license/activate`, data);
      toast.success(t("Licentie geactiveerd voor de hele organisatie."));
      await laden();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("Dit licentiebestand is niet geldig. Er is niets gewijzigd."));
    } finally {
      setBezig(false);
      if (kiezer.current) kiezer.current.value = "";
    }
  };

  return (
    <div className="space-y-2" data-testid="licentie">
      <h3 className="text-sm font-medium">{t("Licentie")}</h3>
      <p className={`text-sm ${status?.stand === "respijt" ? "text-[hsl(var(--accent))]" : ""}`} data-testid="licentie-regel">
        {status ? licentieRegel(status, t, taal) : t("De licentie kon niet worden gelezen.")}
      </p>
      <p className="text-xs text-muted-foreground">
        {t("De licentie geldt voor iedereen in deze organisatie.")}
      </p>
      {eigenaar ? (
        <>
          <input ref={kiezer} type="file" accept=".json,application/json" className="hidden"
                 data-testid="licentie-bestand" onChange={(e) => activeer(e.target.files?.[0])} />
          <Button variant="outline" disabled={bezig} onClick={() => kiezer.current?.click()} data-testid="licentie-activeer">
            {t("Licentiebestand activeren")}
          </Button>
        </>
      ) : (
        <p className="text-xs text-muted-foreground" data-testid="licentie-alleen-eigenaar">
          {t("Alleen een eigenaar kan een licentie activeren.")}
        </p>
      )}
    </div>
  );
}

/** De melding voor iedereen zolang het respijt loopt (keuze 5). Verder niets. */
export function LicentieMelding() {
  const { t, taal } = useTaal();
  const [status, setStatus] = useState(null);
  useEffect(() => {
    let weg = false;
    axios.get(`${API}/license/status`).then(({ data }) => { if (!weg) setStatus(data); }).catch(() => {});
    return () => { weg = true; };
  }, []);
  if (status?.stand !== "respijt") return null;
  return (
    <div role="status" className="border-b bg-[hsl(var(--muted))] px-4 py-2 text-sm" data-testid="licentie-melding">
      {licentieRegel(status, t, taal)}
    </div>
  );
}
